import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { useAuth } from '../context/AuthContext';
import { businessAPI, usersAPI, barbersAPI } from '../lib/api';import { formatDate, userRoleLabels, daysOfWeek, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Settings, 
  Building,
  Users,
  Clock,
  User,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Save,
  MoreHorizontal,
  UserX,
  UserCheck,
  AlertTriangle,
  Printer,
  KeyRound,
  Usb,
  CheckCircle2,
  XCircle,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import printerService from '../lib/printerService';
import { buildTestPrint } from '../lib/escpos';
import { PinManagerTab } from '../components/PinManagerTab';
import { RecoveredEmployeesCard } from '../components/RecoveredEmployeesCard';

const DEFAULT_WORK_SCHEDULE = {
  monday:    { is_working: true,  start_time: '09:00', end_time: '20:00' },
  tuesday:   { is_working: true,  start_time: '09:00', end_time: '20:00' },
  wednesday: { is_working: true,  start_time: '09:00', end_time: '20:00' },
  thursday:  { is_working: true,  start_time: '09:00', end_time: '20:00' },
  friday:    { is_working: true,  start_time: '09:00', end_time: '20:00' },
  saturday:  { is_working: true,  start_time: '09:00', end_time: '18:00' },
  sunday:    { is_working: false, start_time: '09:00', end_time: '18:00' },
};

export default function ConfiguracionPage() {
  const { user: authUser, selectedBusiness, businesses, refreshUser, isAdmin } = useAuth();
  const isMaster = !!authUser?.is_master_admin;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dedupeRunning, setDedupeRunning] = useState(false);
  const [business, setBusiness] = useState(null);
  const [users, setUsers] = useState([]);
  const [barbers, setBarbers] = useState([]);
  
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [barberDialogOpen, setBarberDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBarber, setSelectedBarber] = useState(null);

  // Printer state
  const [printerInfo, setPrinterInfo] = useState(null); // { vendorId, productId, productName }
  const [printerBusy, setPrinterBusy] = useState(false);
  const [autoPrint, setAutoPrint] = useState(() => {
    try {
      return localStorage.getItem('thermal_printer_autoprint') !== 'false';
    } catch {
      return true;
    }
  });

  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'barber'
  });

  const [barberForm, setBarberForm] = useState({
    user_id: '',
    display_name: '',
    email: '',
    phone: '',
    pin_code: '',
    specialties: '',
    bio: '',
    commission_rate: 0,
    commission_fixed: 0,
    product_commission_rate: 0,
    profile_type: 'barber',
    employee_type: 'Barbería',
    pos_shared_business_ids: [],
    rest_days: ['sunday'],
    work_schedule: { ...DEFAULT_WORK_SCHEDULE }
  });

  useEffect(() => {
    const loadData = async () => {
      if (!selectedBusiness) return;
      
      try {
        setLoading(true);
        const [businessRes, usersRes, barbersRes] = await Promise.all([
          businessAPI.get(selectedBusiness.id),
          usersAPI.getAll(),
          barbersAPI.getAll({ include_private: true })
        ]);
        
        setBusiness(businessRes.data);
        setUsers(usersRes.data);
        setBarbers(barbersRes.data);
      } catch (error) {
        logger.error('Error loading config:', error);
        toast.error('Error al cargar configuración');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedBusiness]);

  // Try silent reconnect on page mount
  useEffect(() => {
    (async () => {
      const r = await printerService.reconnectPrinter();
      if (r) setPrinterInfo(r);
      else {
        const fp = printerService.getSavedFingerprint();
        if (fp) setPrinterInfo({ ...fp, _stale: true });
      }
    })();
  }, []);

  const handlePairPrinter = async () => {
    if (!navigator.usb) {
      toast.error('WebUSB no disponible. Usa Chrome o Edge.');
      return;
    }
    setPrinterBusy(true);
    try {
      const info = await printerService.pairPrinter();
      setPrinterInfo(info);
      toast.success(`Impresora conectada: ${info.productName || 'OK'}`);
    } catch (err) {
      const msg = err?.message || 'No se pudo conectar';
      if (!String(msg).toLowerCase().includes('no device selected')) {
        toast.error(msg);
      }
    } finally {
      setPrinterBusy(false);
    }
  };

  const handleTestPrint = async () => {
    setPrinterBusy(true);
    try {
      const bytes = buildTestPrint(business?.name || selectedBusiness?.name || 'POS');
      await printerService.printRaw(bytes);
      toast.success('Prueba enviada a la impresora');
    } catch (err) {
      toast.error(err?.message || 'No se pudo imprimir');
    } finally {
      setPrinterBusy(false);
    }
  };

  const handleDisconnectPrinter = async () => {
    await printerService.disconnectPrinter();
    setPrinterInfo(null);
    toast.success('Impresora desconectada');
  };

  const toggleAutoPrint = (checked) => {
    setAutoPrint(checked);
    try {
      localStorage.setItem('thermal_printer_autoprint', checked ? 'true' : 'false');
    } catch { /* ignore */ }
  };

  const handleSaveBusiness = async () => {
    setSaving(true);
    try {
      await businessAPI.update(business.id, {
        name: business.name,
        address: business.address,
        phone: business.phone,
        email: business.email,
        schedule: business.schedule
      });
      toast.success('Configuración guardada');
      refreshUser();
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSchedule = (day, field, value) => {
    setBusiness((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: {
          ...prev.schedule?.[day],
          [field]: value
        }
      }
    }));
  };

  const toggleScheduleDay = (day, checked) => {
    setBusiness((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [day]: checked
          ? { open: '09:00', close: '20:00' }
          : { open: 'closed', close: 'closed' }
      }
    }));
  };

  const openUserDialog = (user = null) => {
    setSelectedUser(user);
    if (user) {
      setUserForm({
        email: user.email,
        password: '',
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone || '',
        role: user.role
      });
    } else {
      setUserForm({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        role: 'barber'
      });
    }
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.email || !userForm.first_name || !userForm.last_name) {
      toast.error('Completa los campos requeridos');
      return;
    }

    try {
      if (selectedUser) {
        await usersAPI.update(selectedUser.id, {
          first_name: userForm.first_name,
          last_name: userForm.last_name,
          phone: userForm.phone,
          role: userForm.role
        });
        toast.success('Usuario actualizado');
      } else {
        if (!userForm.password) {
          toast.error('La contraseña es requerida');
          return;
        }
        await usersAPI.create(userForm);
        toast.success('Usuario creado');
      }
      setUserDialogOpen(false);
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const [userToDelete, setUserToDelete] = useState(null);

  const handleDeactivateUser = async (user) => {
    try {
      await usersAPI.delete(user.id);
      toast.success(`${user.first_name || 'Usuario'} desactivado`);
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      toast.error('Error al desactivar');
    }
  };

  const handleReactivateUser = async (user) => {
    try {
      await usersAPI.update(user.id, { is_active: true });
      toast.success(`${user.first_name || 'Usuario'} reactivado`);
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      toast.error('Error al reactivar');
    }
  };

  const handleHardDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await usersAPI.hardDelete(userToDelete.id);
      toast.success('Usuario eliminado permanentemente');
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al borrar');
    } finally {
      setUserToDelete(null);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm('¿Estás seguro de desactivar este usuario?')) return;
    
    try {
      await usersAPI.delete(user.id);
      toast.success('Usuario desactivado');
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openBarberDialog = (barber = null) => {
    setSelectedBarber(barber);
    if (barber) {
      const restDays = Array.isArray(barber.rest_days) && barber.rest_days.length
        ? barber.rest_days
        : (barber.rest_day ? [barber.rest_day] : []);
      setBarberForm({
        user_id: barber.user_id,
        display_name: barber.display_name,
        email: barber.email || '',
        phone: barber.phone || '',
        pin_code: barber.pin_code || '',
        specialties: barber.specialties?.join(', ') || '',
        bio: barber.bio || '',
        commission_rate: barber.commission_rate || 0,
        commission_fixed: barber.commission_fixed || 0,
        product_commission_rate: barber.product_commission_rate || 0,
        is_private: !!barber.is_private,
        profile_type: barber.profile_type || 'barber',
        employee_type: barber.employee_type || 'Barbería',
        pos_shared_business_ids: Array.isArray(barber.pos_shared_business_ids) ? barber.pos_shared_business_ids : [],
        rest_days: restDays,
        work_schedule: {
          ...DEFAULT_WORK_SCHEDULE,
          ...(barber.work_schedule || {}),
        }
      });
    } else {
      setBarberForm({
        user_id: '',
        display_name: '',
        email: '',
        phone: '',
        pin_code: '',
        specialties: '',
        bio: '',
        commission_rate: 0,
        commission_fixed: 0,
        product_commission_rate: 0,
        is_private: false,
        profile_type: 'barber',
        employee_type: 'Barbería',
        pos_shared_business_ids: [],
        rest_days: ['sunday'],
        work_schedule: { ...DEFAULT_WORK_SCHEDULE }
      });
    }
    setBarberDialogOpen(true);
  };

  const handleSaveBarber = async () => {
    if (!barberForm.display_name) {
      toast.error('El nombre es requerido');
      return;
    }
    if (barberForm.pin_code && !/^\d{4}$/.test(barberForm.pin_code.trim())) {
      toast.error('El PIN debe tener exactamente 4 dígitos');
      return;
    }

    try {
      const data = {
        ...barberForm,
        pin_code: (barberForm.pin_code || '').trim(),
        email: (barberForm.email || '').trim() || null,
        specialties: barberForm.specialties.split(',').map(s => s.trim()).filter(Boolean),
        business_id: selectedBusiness.id
      };
      if (!isMaster) delete data.pin_code;

      if (selectedBarber) {
        await barbersAPI.update(selectedBarber.id, data);
        toast.success('Barbero actualizado');
      } else {
        await barbersAPI.create(data);
        toast.success('Barbero creado');
      }
      setBarberDialogOpen(false);
      const res = await barbersAPI.getAll({ include_private: true });
      setBarbers(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (Array.isArray(detail) ? (detail[0]?.msg || 'Datos inválidos') : 'Error al guardar'));
    }
  };

  const handleDeleteBarber = async (barber) => {
    if (!window.confirm('¿Estás seguro de eliminar este barbero?')) return;
    
    try {
      await barbersAPI.delete(barber.id);
      toast.success('Barbero eliminado');
      const res = await barbersAPI.getAll({ include_private: true });
      setBarbers(res.data);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleDeduplicateAll = async () => {
    if (!businesses || businesses.length === 0) {
      toast.error('No hay negocios disponibles');
      return;
    }
    const confirmMsg =
      `Se ejecutará la limpieza de barberos duplicados en ${businesses.length} negocio(s): ` +
      businesses.map((b) => b.name).join(', ') +
      '. ¿Continuar?';
    if (!window.confirm(confirmMsg)) return;

    setDedupeRunning(true);
    const results = [];
    let totalRemoved = 0;
    let totalGroups = 0;
    let totalRemappedSales = 0;
    for (const biz of businesses) {
      try {
        const res = await barbersAPI.deduplicate(biz.id);
        const data = res.data || {};
        totalRemoved += data.removed || 0;
        totalGroups += data.groups || 0;
        totalRemappedSales += data.remapped_sales || 0;
        results.push({ name: biz.name, ok: true, ...data });
      } catch (err) {
        logger.error('Dedupe failed for business', biz.id, err);
        results.push({
          name: biz.name,
          ok: false,
          error: err?.response?.data?.detail || err?.message || 'Error',
        });
      }
    }
    setDedupeRunning(false);

    // Refresh the current business barbers list
    try {
      const refreshed = await barbersAPI.getAll({ include_private: true });
      setBarbers(refreshed.data);
    } catch (_) {
      // non-fatal
    }

    const failed = results.filter((r) => !r.ok);
    const summary =
      `Deduplicación finalizada. Grupos: ${totalGroups}, Eliminados: ${totalRemoved}, ` +
      `Ventas remapeadas: ${totalRemappedSales}.`;
    if (failed.length === 0) {
      toast.success(summary);
    } else {
      toast.warning(
        `${summary} Fallaron ${failed.length}: ${failed.map((f) => f.name).join(', ')}`
      );
    }
    // Also log full detail for the admin.
    logger.info('Dedupe results:', results);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  // Get users that are barbers but don't have a barber profile
  const availableUsers = users.filter(u => 
    ['barber', 'stylist'].includes(u.role) && 
    !barbers.find(b => b.user_id === u.id)
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="configuracion-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Configuración
        </h1>
        <p className="text-slate-500 mt-1">
          Administra {selectedBusiness?.name}
        </p>
      </div>

      <Tabs defaultValue="business">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="business" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <Building className="w-4 h-4 mr-2" />
            Negocio
          </TabsTrigger>
          <TabsTrigger value="schedule" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <Clock className="w-4 h-4 mr-2" />
            Horarios
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <Users className="w-4 h-4 mr-2" />
            Usuarios
          </TabsTrigger>
          <TabsTrigger value="barbers" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <User className="w-4 h-4 mr-2" />
            Barberos
          </TabsTrigger>
          <TabsTrigger value="printer" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="config-tab-printer">
            <Printer className="w-4 h-4 mr-2" />
            Impresora
          </TabsTrigger>
          {isMaster && (
            <TabsTrigger value="pins" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="config-tab-pins">
              <KeyRound className="w-4 h-4 mr-2" />
              PINs
            </TabsTrigger>
          )}
        </TabsList>

        {isMaster && (
          <TabsContent value="pins" className="mt-6">
            <PinManagerTab />
          </TabsContent>
        )}

        {/* Business Info */}
        <TabsContent value="business" className="mt-4">
          <Card className="border-slate-200 bg-white">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Información del negocio</CardTitle>
              <CardDescription>Datos generales de {business?.name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre del negocio</Label>
                  <Input
                    value={business?.name || ''}
                    onChange={(e) => setBusiness({ ...business, name: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Input
                    value="Barbería y Salón"
                    disabled
                    className="bg-slate-100 border-slate-200"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input
                  value={business?.address || ''}
                  onChange={(e) => setBusiness({ ...business, address: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                  placeholder="Calle, número, colonia, ciudad"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={business?.phone || ''}
                    onChange={(e) => setBusiness({ ...business, phone: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    placeholder="+52 55 1234 5678"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Correo</Label>
                  <Input
                    type="email"
                    value={business?.email || ''}
                    onChange={(e) => setBusiness({ ...business, email: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    placeholder="contacto@negocio.com"
                  />
                </div>
              </div>

              <Button onClick={handleSaveBusiness} className="bg-primary text-white hover:bg-primary/90" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar cambios
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schedule */}
        <TabsContent value="schedule" className="mt-4">
          <Card className="border-slate-200 bg-white">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Horarios de atención</CardTitle>
              <CardDescription>Configura los horarios de apertura y cierre</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {daysOfWeek.map(({ key, label }) => {
                  const dayConfig = business?.schedule?.[key] || {};
                  const isClosed = dayConfig.open === 'closed' || dayConfig.open === undefined;
                  const openVal = isClosed ? '09:00' : (dayConfig.open || '09:00');
                  const closeVal = isClosed ? '20:00' : (dayConfig.close || '20:00');
                  return (
                  <div key={key} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50" data-testid={`config-schedule-row-${key}`}>
                    <span className="w-24 font-medium">{label}</span>
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={openVal}
                        onChange={(e) => handleUpdateSchedule(key, 'open', e.target.value)}
                        className="w-32 bg-slate-100 border-slate-200"
                        disabled={isClosed}
                        data-testid={`config-schedule-open-${key}`}
                      />
                      <span className="text-slate-500">a</span>
                      <Input
                        type="time"
                        value={closeVal}
                        onChange={(e) => handleUpdateSchedule(key, 'close', e.target.value)}
                        className="w-32 bg-slate-100 border-slate-200"
                        disabled={isClosed}
                        data-testid={`config-schedule-close-${key}`}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!isClosed}
                        onCheckedChange={(checked) => toggleScheduleDay(key, checked)}
                        data-testid={`config-schedule-switch-${key}`}
                      />
                      <span className="text-sm text-slate-500">
                        {isClosed ? 'Cerrado' : 'Abierto'}
                      </span>
                    </div>
                  </div>
                  );
                })}
              </div>

              <Button onClick={handleSaveBusiness} className="mt-4 bg-primary text-white hover:bg-primary/90" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Guardar horarios
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-4">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Usuarios</CardTitle>
                <CardDescription>Gestiona los usuarios del sistema</CardDescription>
              </div>
              {isMaster && (
              <Button onClick={() => openUserDialog()} className="bg-primary text-white hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo usuario
              </Button>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Correo</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="w-32 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow key={user.id} className="animate-fade-in" style={{ animationDelay: `${index * 0.03}s` }}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-slate-200 text-white text-xs">
                              {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{user.first_name} {user.last_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-500">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-slate-500 border-slate-200">
                          {userRoleLabels[user.role]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            user.is_active 
                              ? "text-emerald-600 border-emerald-300 bg-emerald-50" 
                              : "text-slate-500 border-slate-200 bg-slate-50"
                          )}
                        >
                          {user.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end items-center gap-1">
                          {isMaster && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openUserDialog(user)}
                            title="Editar"
                            data-testid={`user-edit-${user.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          )}
                          {isMaster && !user.is_master_admin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  data-testid={`user-actions-${user.id}`}
                                >
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {user.is_active ? (
                                  <DropdownMenuItem
                                    onClick={() => handleDeactivateUser(user)}
                                    className="cursor-pointer"
                                    data-testid={`user-deactivate-${user.id}`}
                                  >
                                    <UserX className="w-4 h-4 mr-2 text-amber-600" />
                                    <span>Desactivar usuario</span>
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={() => handleReactivateUser(user)}
                                    className="cursor-pointer"
                                    data-testid={`user-reactivate-${user.id}`}
                                  >
                                    <UserCheck className="w-4 h-4 mr-2 text-emerald-600" />
                                    <span>Reactivar usuario</span>
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setUserToDelete(user)}
                                  className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
                                  data-testid={`user-delete-${user.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  <span>Borrar permanentemente</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Barbers */}
        <TabsContent value="barbers" className="mt-4">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle style={{ fontFamily: 'Manrope' }}>Barberos / Estilistas</CardTitle>
                <CardDescription>Perfiles de trabajo y comisiones</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin() && (
                  <Button
                    onClick={handleDeduplicateAll}
                    disabled={dedupeRunning}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="dedupe-barbers-btn"
                    title="Ejecuta POST /api/barbers/deduplicate en cada negocio para colapsar barberos duplicados y remapear sus ventas."
                  >
                    {dedupeRunning ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Wand2 className="w-4 h-4 mr-2" />
                    )}
                    Limpiar duplicados
                  </Button>
                )}
                {isMaster && (
                <Button onClick={() => openBarberDialog()} className="bg-primary text-white hover:bg-primary/90">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo barbero
                </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isMaster && (
                <RecoveredEmployeesCard
                  onChanged={() => barbersAPI.getAll({ include_private: true }).then((res) => setBarbers(res.data)).catch(() => {})}
                />
              )}
              {barbers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay barberos registrados</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {barbers.map((barber, index) => (
                    <div 
                      key={barber.id}
                      className="p-4 rounded-lg bg-slate-50 animate-fade-in"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-slate-200 text-white">
                              {barber.display_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className="font-semibold flex items-center gap-2 flex-wrap">
                              {barber.display_name}
                              {barber.is_private && (
                                <Badge className="bg-slate-900 text-white text-[10px] px-1.5 py-0" data-testid={`private-badge-${barber.id}`}>
                                  🔒 Privado
                                </Badge>
                              )}
                              <Badge
                                className={cn(
                                  "text-white text-[10px] px-1.5 py-0",
                                  barber.employee_type === 'Salón' ? "bg-rose-600" : "bg-amber-600"
                                )}
                                data-testid={`employee-type-badge-${barber.id}`}
                              >
                                {barber.employee_type || 'Barbería'}
                              </Badge>
                              {barber.recovered && (
                                <Badge className="bg-emerald-600 text-white text-[10px] px-1.5 py-0" data-testid={`recovered-badge-${barber.id}`}>
                                  Recuperado
                                </Badge>
                              )}
                              {barber.profile_type === 'admin_collaborator' && (
                                <Badge
                                  className="bg-indigo-600 text-white text-[10px] px-1.5 py-0"
                                  data-testid={`admin-collab-badge-${barber.id}`}
                                >
                                  Colaborador Administrativo; Ventas
                                </Badge>
                              )}
                              {Array.isArray(barber.pos_shared_business_ids) && barber.pos_shared_business_ids.length > 0 && (
                                <Badge
                                  className="bg-emerald-600 text-white text-[10px] px-1.5 py-0"
                                  data-testid={`pos-shared-badge-${barber.id}`}
                                >
                                  🔀 Compartido POS ({barber.pos_shared_business_ids.length})
                                </Badge>
                              )}
                            </h4>
                            <Badge 
                              variant="outline" 
                              className={cn(
                                "text-[10px]",
                                barber.is_available ? "text-emerald-400 border-emerald-500/30" : "text-slate-500"
                              )}
                            >
                              {barber.is_available ? 'Disponible' : 'No disponible'}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openBarberDialog(barber)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          {isMaster && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7 hover:text-red-400"
                            onClick={() => handleDeleteBarber(barber)}
                            data-testid={`barber-delete-${barber.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                          )}
                        </div>
                      </div>

                      {barber.specialties?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {barber.specialties.map(spec => (
                            <Badge key={spec} variant="outline" className="text-[10px] text-slate-500">
                              {spec}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-slate-500">Comisión %</span>
                          <span>{barber.commission_rate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Comisión fija</span>
                          <span>${barber.commission_fixed}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Printer */}
        <TabsContent value="printer" className="mt-4">
          <Card className="border-slate-200 bg-white">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }} className="flex items-center gap-2">
                <Printer className="w-5 h-5" />
                Impresora térmica USB
              </CardTitle>
              <CardDescription>
                Conecta una impresora ESC/POS de 58 mm (Xprinter XP-58IIH y compatibles) para imprimir tickets del POS.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Status */}
              <div
                className={cn(
                  'flex items-start gap-3 p-4 rounded-lg border',
                  printerService.isConnected()
                    ? 'bg-emerald-50 border-emerald-200'
                    : printerInfo
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-slate-50 border-slate-200'
                )}
                data-testid="printer-status"
              >
                {printerService.isConnected() ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                ) : printerInfo ? (
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-slate-400 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-slate-900">
                    {printerService.isConnected()
                      ? 'Impresora conectada'
                      : printerInfo
                      ? 'Impresora emparejada (no conectada)'
                      : 'Sin impresora conectada'}
                  </p>
                  {printerInfo && (
                    <p className="text-sm text-slate-600 mt-0.5">
                      {printerInfo.manufacturerName || ''} {printerInfo.productName || ''}{' '}
                      <span className="text-slate-400 font-mono">
                        (VID 0x{printerInfo.vendorId?.toString(16).padStart(4, '0')} /
                        PID 0x{printerInfo.productId?.toString(16).padStart(4, '0')})
                      </span>
                    </p>
                  )}
                  {!printerService.isConnected() && printerInfo && (
                    <p className="text-xs text-amber-700 mt-1">
                      La impresora fue emparejada antes pero no responde. Verifica que esté encendida y conectada al USB, o vuelve a conectarla.
                    </p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handlePairPrinter}
                  className="bg-primary text-white hover:bg-primary/90"
                  disabled={printerBusy}
                  data-testid="printer-pair-btn"
                >
                  {printerBusy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Usb className="w-4 h-4 mr-2" />
                  )}
                  {printerInfo ? 'Volver a conectar / cambiar impresora' : 'Conectar impresora USB'}
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-200"
                  onClick={handleTestPrint}
                  disabled={printerBusy || !printerInfo}
                  data-testid="printer-test-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir prueba
                </Button>
                {printerInfo && (
                  <Button
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={handleDisconnectPrinter}
                    disabled={printerBusy}
                    data-testid="printer-disconnect-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Olvidar impresora
                  </Button>
                )}
              </div>

              {/* Auto-print toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div>
                  <p className="font-medium text-slate-900">Impresión automática</p>
                  <p className="text-xs text-slate-500">
                    Imprime el ticket apenas se completa una venta en el POS.
                  </p>
                </div>
                <Switch
                  checked={autoPrint}
                  onCheckedChange={toggleAutoPrint}
                  data-testid="printer-autoprint-switch"
                />
              </div>

              {/* Help */}
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 space-y-2">
                <p className="font-medium text-slate-900">Pasos para conectar (Chrome / Edge)</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Conecta la impresora al puerto USB y enciéndela.</li>
                  <li>Haz clic en <span className="font-medium">Conectar impresora USB</span>.</li>
                  <li>En el diálogo del navegador, selecciona la <span className="font-medium">Xprinter XP-58IIH</span> (o el modelo correspondiente) y presiona <span className="font-medium">Conectar</span>.</li>
                  <li>Haz clic en <span className="font-medium">Imprimir prueba</span> para validar.</li>
                </ol>
                <p className="text-xs text-amber-700 pt-2">
                  <span className="font-semibold">En Windows:</span> si la impresora ya está instalada como impresora del sistema, WebUSB no podrá usarla. Instala el driver <span className="font-mono">WinUSB</span> con la herramienta gratuita <span className="font-mono">Zadig</span> (zadig.akeo.ie) seleccionando tu impresora en la lista.
                </p>
                <p className="text-xs text-slate-500">
                  Compatible con ESC/POS 58 mm (Xprinter, Hoin, 3nStar, Epson TM-T20, Star TSP, etc.).
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {selectedUser ? 'Editar usuario' : 'Nuevo usuario'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={userForm.first_name}
                  onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label>Apellido *</Label>
                <Input
                  value={userForm.last_name}
                  onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Correo *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="bg-slate-100 border-slate-200"
                disabled={!!selectedUser}
              />
            </div>

            {!selectedUser && (
              <div className="space-y-2">
                <Label>Contraseña *</Label>
                <Input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={userForm.phone}
                  onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                  <SelectTrigger className="bg-slate-100 border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="barber">Barbero</SelectItem>
                    <SelectItem value="stylist">Estilista</SelectItem>
                    <SelectItem value="client">Cliente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleSaveUser} className="bg-primary text-white hover:bg-primary/90">
              {selectedUser ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Barber Dialog */}
      <Dialog open={barberDialogOpen} onOpenChange={setBarberDialogOpen}>
        <DialogContent className="bg-white border-slate-200 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              {selectedBarber ? 'Editar barbero' : 'Nuevo barbero'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {!selectedBarber && availableUsers.length > 0 && (
              <div className="space-y-2">
                <Label>Usuario asociado</Label>
                <Select value={barberForm.user_id} onValueChange={(value) => {
                  const user = users.find(u => u.id === value);
                  setBarberForm({ 
                    ...barberForm, 
                    user_id: value,
                    display_name: user ? `${user.first_name} ${user.last_name}` : ''
                  });
                }}>
                  <SelectTrigger className="bg-slate-100 border-slate-200">
                    <SelectValue placeholder="Seleccionar usuario" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {availableUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid grid-cols-3 w-full bg-slate-100">
                <TabsTrigger value="info" data-testid="barber-tab-info">
                  <User className="w-4 h-4 mr-1.5" />
                  Información
                </TabsTrigger>
                <TabsTrigger value="schedule" data-testid="barber-tab-schedule">
                  <Clock className="w-4 h-4 mr-1.5" />
                  Horario
                </TabsTrigger>
                <TabsTrigger value="commissions" data-testid="barber-tab-commissions">
                  <Settings className="w-4 h-4 mr-1.5" />
                  Comisiones
                </TabsTrigger>
              </TabsList>

              {/* ─── INFORMACIÓN ─────────────────────────────────────── */}
              <TabsContent value="info" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Nombre a mostrar *</Label>
                  <Input
                    value={barberForm.display_name}
                    onChange={(e) => setBarberForm({ ...barberForm, display_name: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    placeholder="Nombre que verán los clientes"
                    data-testid="barber-display-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="barber-employee-type">
                    {['Barbería', 'Salón'].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setBarberForm({
                          ...barberForm,
                          employee_type: t,
                          profile_type: barberForm.profile_type === 'admin_collaborator'
                            ? 'admin_collaborator'
                            : (t === 'Salón' ? 'stylist' : 'barber'),
                        })}
                        className={cn(
                          "h-10 rounded-md border text-sm font-medium transition-colors",
                          (barberForm.employee_type || 'Barbería') === t
                            ? (t === 'Salón' ? "bg-rose-600 border-rose-600 text-white" : "bg-amber-600 border-amber-600 text-white")
                            : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                        )}
                        data-testid={`barber-employee-type-${t === 'Salón' ? 'salon' : 'barberia'}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Tipo de perfil *</Label>
                  <Select
                    value={barberForm.profile_type || 'barber'}
                    onValueChange={(v) => setBarberForm({ ...barberForm, profile_type: v })}
                  >
                    <SelectTrigger
                      className="bg-slate-100 border-slate-200"
                      data-testid="barber-profile-type"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="barber">Barbero</SelectItem>
                      <SelectItem value="stylist">Estilista</SelectItem>
                      <SelectItem value="admin_collaborator">
                        Colaborador Administrativo; Ventas
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {barberForm.profile_type === 'admin_collaborator' && (
                    <p className="text-xs text-slate-500 leading-snug">
                      Perfil solo de ventas: no aparece en la agenda ni en el
                      sistema público de citas ni lleva asistencia (checador),
                      pero sí se puede seleccionar en el POS para generar
                      comisiones como cualquier otro colaborador.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Correo electrónico</Label>
                    <Input
                      type="email"
                      value={barberForm.email}
                      onChange={(e) => setBarberForm({ ...barberForm, email: e.target.value })}
                      className="bg-slate-100 border-slate-200"
                      placeholder="barbero@ejemplo.com"
                      data-testid="barber-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                      value={barberForm.phone}
                      onChange={(e) => setBarberForm({ ...barberForm, phone: e.target.value })}
                      className="bg-slate-100 border-slate-200"
                      placeholder="55 1234 5678"
                      data-testid="barber-phone"
                    />
                  </div>
                </div>

                {isMaster && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    PIN de acceso al panel
                    <span className="text-xs font-normal text-slate-400">(4 dígitos)</span>
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={barberForm.pin_code}
                    onChange={(e) => setBarberForm({ ...barberForm, pin_code: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    className="bg-slate-100 border-slate-200 font-mono tracking-widest text-center"
                    placeholder="• • • •"
                    data-testid="barber-pin"
                  />
                  <p className="text-xs text-slate-500">
                    El barbero usará este PIN para entrar al sistema desde <span className="font-mono">/login</span>. Déjalo vacío para deshabilitar el acceso.
                  </p>
                </div>
                )}

                <div className="space-y-2">
                  <Label>Especialidades</Label>
                  <Input
                    value={barberForm.specialties}
                    onChange={(e) => setBarberForm({ ...barberForm, specialties: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    placeholder="Cortes, Barba, Color (separados por coma)"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Biografía</Label>
                  <Input
                    value={barberForm.bio}
                    onChange={(e) => setBarberForm({ ...barberForm, bio: e.target.value })}
                    className="bg-slate-100 border-slate-200"
                    placeholder="Breve descripción..."
                  />
                </div>

                {isAdmin() && (
                  <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <div className="min-w-0">
                      <Label className="flex items-center gap-2 font-medium">
                        🔒 Barbero privado
                      </Label>
                      <p className="text-xs text-slate-500 mt-1 leading-snug">
                        Sus citas se ocultan de la agenda pública (<span className="font-mono">/agendar</span>),
                        de la agenda principal (<span className="font-mono">/agenda</span>) y de los reportes
                        generales. Solo el Master Admin puede administrarlas desde el botón
                        <b> &quot;Agenda privada&quot;</b> dentro de la agenda.
                      </p>
                    </div>
                    <Switch
                      checked={!!barberForm.is_private}
                      onCheckedChange={(v) => setBarberForm({ ...barberForm, is_private: v })}
                      data-testid="barber-is-private-switch"
                    />
                  </div>
                )}

                {Array.isArray(businesses) && businesses.filter((b) => b.id !== selectedBusiness?.id).length > 0 && (
                  <div className="space-y-2 pt-2 border-t border-slate-200">
                    <Label className="flex items-center gap-2 font-medium">
                      🔀 También aparece en el POS de otros negocios
                    </Label>
                    <p className="text-xs text-slate-500 leading-snug">
                      Marca los negocios donde este colaborador debe
                      aparecer en el selector del POS para cobrar servicios
                      o productos y acumular comisiones. No le da acceso a
                      la agenda, al sistema público de citas ni al checador
                      de esos negocios.
                    </p>
                    <div className="flex flex-col gap-2">
                      {businesses
                        .filter((b) => b.id !== selectedBusiness?.id)
                        .map((b) => {
                          const checked = (barberForm.pos_shared_business_ids || []).includes(b.id);
                          return (
                            <label
                              key={b.id}
                              className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                checked={checked}
                                onChange={(e) => {
                                  const current = new Set(barberForm.pos_shared_business_ids || []);
                                  if (e.target.checked) current.add(b.id);
                                  else current.delete(b.id);
                                  setBarberForm({
                                    ...barberForm,
                                    pos_shared_business_ids: Array.from(current),
                                  });
                                }}
                                data-testid={`barber-pos-shared-${b.id}`}
                              />
                              <span>{b.name}</span>
                            </label>
                          );
                        })}
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* ─── HORARIO ─────────────────────────────────────────── */}
              <TabsContent value="schedule" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Horario de trabajo semanal</Label>
                  <p className="text-xs text-slate-500">
                    Define los días y horas que el barbero atiende. Los días desmarcados aparecen como descanso en la agenda.
                  </p>
                </div>

                <div
                  className="space-y-2 rounded-md border border-slate-200 bg-white p-3"
                  data-testid="config-barber-work-schedule"
                >
                  {daysOfWeek.map((d) => {
                    const day = barberForm.work_schedule?.[d.key] || {};
                    const isRest = (barberForm.rest_days || []).includes(d.key);
                    const working = day.is_working !== false && !isRest;
                    return (
                      <div
                        key={d.key}
                        className={`flex items-center gap-3 px-3 py-2 rounded-md ${working ? 'bg-emerald-50/50' : 'bg-slate-50'}`}
                      >
                        <label className="flex items-center gap-2 min-w-[120px] cursor-pointer">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                            checked={working}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const restSet = new Set(barberForm.rest_days || []);
                              if (checked) restSet.delete(d.key);
                              else restSet.add(d.key);
                              setBarberForm({
                                ...barberForm,
                                rest_days: Array.from(restSet),
                                work_schedule: {
                                  ...barberForm.work_schedule,
                                  [d.key]: {
                                    ...day,
                                    is_working: checked,
                                    start_time: day.start_time || '09:00',
                                    end_time: day.end_time || '20:00',
                                  },
                                },
                              });
                            }}
                            data-testid={`schedule-day-${d.key}`}
                          />
                          <span className={`text-sm font-medium ${working ? 'text-slate-800' : 'text-slate-400'}`}>
                            {d.label}
                          </span>
                        </label>
                        <Input
                          type="time"
                          value={day.start_time || '09:00'}
                          disabled={!working}
                          onChange={(e) => setBarberForm({
                            ...barberForm,
                            work_schedule: {
                              ...barberForm.work_schedule,
                              [d.key]: { ...day, start_time: e.target.value },
                            },
                          })}
                          className="bg-white border-slate-200 w-32"
                          data-testid={`schedule-start-${d.key}`}
                        />
                        <span className="text-slate-400 text-sm">a</span>
                        <Input
                          type="time"
                          value={day.end_time || '20:00'}
                          disabled={!working}
                          onChange={(e) => setBarberForm({
                            ...barberForm,
                            work_schedule: {
                              ...barberForm.work_schedule,
                              [d.key]: { ...day, end_time: e.target.value },
                            },
                          })}
                          className="bg-white border-slate-200 w-32"
                          data-testid={`schedule-end-${d.key}`}
                        />
                        {!working && (
                          <span className="ml-auto text-xs text-slate-400 font-medium">Día de descanso</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </TabsContent>

              {/* ─── COMISIONES ──────────────────────────────────────── */}
              <TabsContent value="commissions" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Comisión por servicio (%)</Label>
                    <Input
                      type="number"
                      value={barberForm.commission_rate}
                      onChange={(e) => setBarberForm({ ...barberForm, commission_rate: parseFloat(e.target.value) || 0 })}
                      className="bg-slate-100 border-slate-200"
                      min={0}
                      max={100}
                      data-testid="barber-commission-rate"
                    />
                    <p className="text-xs text-slate-500">% sobre el subtotal de cada servicio que él atienda.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Comisión fija por servicio ($)</Label>
                    <Input
                      type="number"
                      value={barberForm.commission_fixed}
                      onChange={(e) => setBarberForm({ ...barberForm, commission_fixed: parseFloat(e.target.value) || 0 })}
                      className="bg-slate-100 border-slate-200"
                      min={0}
                      data-testid="barber-commission-fixed"
                    />
                    <p className="text-xs text-slate-500">Cantidad fija adicional por cada servicio.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Comisión por producto (%)</Label>
                  <Input
                    type="number"
                    value={barberForm.product_commission_rate}
                    onChange={(e) => setBarberForm({ ...barberForm, product_commission_rate: parseFloat(e.target.value) || 0 })}
                    className="bg-slate-100 border-slate-200"
                    min={0}
                    max={100}
                    data-testid="barber-product-commission"
                  />
                  <p className="text-xs text-slate-500">% sobre el subtotal de cada producto vendido por este barbero.</p>
                </div>

                <div className="space-y-2">
                  <Label>Días de descanso</Label>
                  <div
                    className="grid grid-cols-2 gap-2 rounded-md border border-slate-200 bg-white p-3"
                    data-testid="config-barber-rest-days"
                  >
                    {daysOfWeek.map((d) => {
                      const checked = (barberForm.rest_days || []).includes(d.key);
                      return (
                        <label
                          key={d.key}
                          className={`flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors ${checked ? 'bg-red-50 text-red-700 border border-red-200' : 'hover:bg-slate-50 border border-transparent'}`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500"
                            checked={checked}
                            onChange={() => {
                              const set = new Set(barberForm.rest_days || []);
                              if (set.has(d.key)) set.delete(d.key);
                              else set.add(d.key);
                              setBarberForm({ ...barberForm, rest_days: Array.from(set) });
                            }}
                            data-testid={`config-barber-rest-day-${d.key}`}
                          />
                          <span className="text-sm font-medium">{d.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-400">Marca todos los días en que el barbero NO trabaja.</p>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBarberDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleSaveBarber} className="bg-primary text-white hover:bg-primary/90">
              {selectedBarber ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm permanent user deletion */}
      <AlertDialog open={!!userToDelete} onOpenChange={(o) => !o && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Borrar usuario permanentemente
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará{' '}
              <span className="font-medium text-slate-900">
                {userToDelete?.first_name} {userToDelete?.last_name}
              </span>{' '}
              solo como usuario (pierde su acceso). Su perfil de empleado, citas, ventas y comisiones se conservan.
              <br /><br />
              ¿Estás seguro? Si solo quieres bloquear su acceso temporalmente, usa <span className="font-medium">Desactivar usuario</span> en su lugar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDeleteUser}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="user-delete-confirm"
            >
              Sí, borrar permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
